import { DobPipe } from '../pipes/dob.pipe';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild, ElementRef } from '@angular/core';
import { BatchService, ScanMatchMethod, ScanMatchStatus } from '../Services/batch.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import {
  ProductsService,
  BarcodeLookupResult,
  DrugNameSuggestion,
} from '../Services/products.service';
import { environment } from './../../environments/environment';
import { RichTextLine } from '../models/ns-tray-rich-text.models';
import { Subscription } from 'rxjs';
import { NsTrayPrintService } from '../Services/ns-tray-print.service';
import { MpsLabelPrinterService } from '../Services/mps-label-printer.service';
import { BagProgress, bagProgress, completeBagIds, labelDate } from '../Services/mps-label-builder';
import { CLOSED_STATUSES, LOCKED_STATUSES, isBatchPick } from '../models/batch-status';
import { BagReprintPanelComponent } from '../bag-reprint-panel/bag-reprint-panel.component';
import { MedBadgesComponent } from '../med-badges/med-badges.component';

@Component({
  selector: 'app-scanmedication',
  standalone: true,
  imports: [DobPipe, CommonModule, FormsModule, BagReprintPanelComponent, MedBadgesComponent],
  templateUrl: './scanmedication.component.html',
  styleUrl: './scanmedication.component.css',
  providers: [BatchService, ProductsService],
})
export class ScanmedicationComponent {
  groupedMedications: any = {};
  batchData: any = {};
  prescriptionIds: string[] = [];
  scanStatuses: any = {};
  batchId: string = '';
  availablePrinters: string[] = [];
  expandedPrescriptionId: string | null = null;
  showPrinterSelectionModal: boolean = false;
  isScanComplete: boolean = false;
  selectedPrinter: string = '';
  currentPrescriptionIndex: number = 0;
  viewOnly = false;
  showBagReprint = false;
  /** MPS bags whose labels printed in this session (or were already complete on load). */
  private printedBagIds = new Set<string>();
  /** Bags sent to the printer and not answered yet, and bags tried since they last completed. */
  private printingBagIds = new Set<string>();
  private triedBagIds = new Set<string>();
  /** Why the wrong-medicine dialog offers no line to switch to (exact-product lines), or ''. */
  exactProductNote = '';
  private trayConnected = false;
  private nsTraySubs = new Subscription();
  showMedicationId = environment.showMedicationId;
  alertMessage = '';
  alertType: 'success' | 'error' | 'warning' = 'success';
  showWrongMedicineModal = false;
  wrongMedicineId = '';
  wrongMedicinePrescriptionId = '';
  wrongMedicineLookup: BarcodeLookupResult | null = null;
  wrongMedicineUnscannedMeds: any[] = [];
  wrongMedicineMatchStatus: ScanMatchStatus | null = null;
  wrongMedicineMatchMethod: ScanMatchMethod | null = null;
  // Add medication form state
  showAddMedicationForm = false;
  addMedForm = { productName: '', manufacturer: '', dosageForm: '', activeSubstances: '', packSize: '' };
  addMedSaving = false;
  drugNameSuggestions: DrugNameSuggestion[] = [];
  manufacturerSuggestions: string[] = [];
  dosageFormOptions: string[] = [];
  allManufacturers: string[] = [];
  addMedWarnings: { drugName?: string; manufacturer?: string } = {};
  private drugNameTimer: any = null;
  @ViewChild('drugNameInput') drugNameInputRef!: ElementRef<HTMLInputElement>;
  printMedication: {
    medicationName: string;
    medicineDosage: string;
    instruction: string;
    medicationId: string;
    quantity: number | null;
    patientName: string;
    labelDate: string;
    /** MPS marking such as (P), printed as sent after the name. */
    marking?: string | null;
  } | null = null;
  printPackLabel: {
    patientLabel: string;
    patientName: string;
    labelDate: string;
    itemCountText: string;
  } | null = null;
  @Output() isScanFinished = new EventEmitter<boolean>();
  @Output() modalClosed = new EventEmitter<void>();
  @Input() isStartBtnClicked: boolean = false;

  get isModalOpen(): boolean {
    return this.showWrongMedicineModal || this.showAddMedicationForm || this.showPrinterSelectionModal || this.showBagReprint;
  }

  /** Sent by MPS: bag labels replace the pack label, and swaps are blocked. */
  get isMpsBatch(): boolean {
    return this.batchData?.source === 'Mps';
  }

  /** Reprint is offered on MPS batches, except refused and cancelled ones (MPS replaces their barcodes). */
  get canReprintBags(): boolean {
    // A Batch Pick batch prints its bag labels at Finish Verifying, once the bags are verified.
    const unverifiedBatchPick = isBatchPick(this.batchData) && !CLOSED_STATUSES.includes(this.batchData?.status);
    return this.isMpsBatch && !LOCKED_STATUSES.includes(this.batchData?.status) && !unverifiedBatchPick;
  }

  trackByBag(_: number, b: BagProgress) { return b.bagId; }

  /** MPS lines and do-not-substitute lines need the prescribed product itself. */
  private isExactProductLine(medicationId: string): boolean {
    if (this.isMpsBatch) return true;
    const rx = this.groupedMedications[this.wrongMedicinePrescriptionId];
    return !!rx?.medicines?.find((m: any) => m.medicationId === medicationId)?.doNotSubstitute;
  }

  /**
   * Whether the scan in the wrong-medicine dialog may be put against this line. Swaps onto
   * exact-product lines are blocked until we agree with McLernons how a different product should
   * reach the bag (the swap code is kept for when they are allowed); the one exception is a scan
   * the server matched exactly to several lines, where the pharmacist only says which one.
   */
  private canTakeScan(medicationId: string): boolean {
    return !this.isExactProductLine(medicationId) || this.wrongMedicineMatchStatus === 'ambiguous';
  }
  @Output() printPackedList = new EventEmitter<void>();

  constructor(
    private _batchservice: BatchService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private _nsTray: NsTrayPrintService,
    private _lookupservice: ProductsService,
    private _bagLabels: MpsLabelPrinterService,
  ) {}

  ngOnInit() {
    this.activatedRoute.paramMap.subscribe((params) => {
      this.batchId = params.get('batchId') || '';
    });

    this.activatedRoute.queryParamMap.subscribe((queryParams) => {
      const mode = queryParams.get('mode');
      this.viewOnly = mode === 'view';
    });

    this.loadBatchData();
    if (!this.viewOnly) {
      this.connectTray();
    }
  }

  private connectTray() {
    if (this.trayConnected) return;
    this.trayConnected = true;
    this._nsTray.connect();
    this.availablePrinters = this._nsTray.availablePrinters;
    this.nsTraySubs.add(this._nsTray.alerts$.subscribe(a => this.showAlert(a.message, a.type)));
    this.nsTraySubs.add(this._nsTray.needsPrinterSelection$.subscribe(() => {
      this.availablePrinters = this._nsTray.availablePrinters;
      this.showPrinterSelectionModal = true;
    }));
  }

  ngOnDestroy() {
    this.nsTraySubs.unsubscribe();
  }

  showAlert(message: string, type: 'success' | 'error' | 'warning' = 'success') {
    this.alertMessage = message;
    this.alertType = type;
    setTimeout(() => { this.alertMessage = ''; }, 3000);
  }

  printPreference() {
    if (this.selectedPrinter) {
      this._nsTray.setPrinter(this.selectedPrinter);
      this.showPrinterSelectionModal = false;
    }
  }

  loadBatchData() {
    this.activatedRoute.paramMap.subscribe((params) => {
      this.batchId = params.get('batchId') || '';
      this._batchservice.getBatchById(this.batchId).subscribe((response: any) => {
        this.batchData = response.result;
        this.groupedMedications = {};
        this.scanStatuses = {};
        this.batchData.prescriptions.forEach((prescription: any) => {
          const key = prescription.rxId;
          this.groupedMedications[key] = {
            patientName: prescription.patientName,
            medicines: prescription.medications,
            pharmacyId: prescription.pharmacyId,
          };
          this.scanStatuses[key] = {};
          prescription.medications.forEach((medication: any) => {
            // Mark as SCANNED if either the legacy flag is set, or BatchPick
            // already picked/verified this medication.
            const ps = medication.pickScanStatus;
            const vs = medication.verifyScanStatus;
            const picked = ps === 'Picked' || ps === 'PickedAsSubstitute' || ps === 1 || ps === 2;
            const verified = vs === 'Verified' || vs === 1;
            this.scanStatuses[key][medication.medicationId] =
              (medication.isAvailable || picked || verified) ? 'SCANNED' : 'PENDING';
          });
        });
        this.prescriptionIds = Object.keys(this.groupedMedications);
        // Bags this PC recorded as printed; with no record (another PC, or before records were
        // kept), bags already complete are taken as labelled earlier. Reprint covers them.
        this.printedBagIds = new Set(this.loadPrintedBags() ?? this.completeBags());
        // A finished batch is view-only however it was opened (a scan would reopen it); an MPS
        // one can still reprint its bag labels. So is a Batch Pick batch: it is scanned and
        // finished on the pick and verify screens.
        if (CLOSED_STATUSES.includes(this.batchData.status) || isBatchPick(this.batchData)) this.viewOnly = true;
        if (this.viewOnly && this.canReprintBags) this.connectTray();
        // If we were deep-linked with ?rx=RX-XXX, jump to that prescription.
        const targetRx = this.activatedRoute.snapshot.queryParamMap.get('rx');
        if (targetRx) {
          const idx = this.prescriptionIds.indexOf(targetRx);
          if (idx !== -1) this.currentPrescriptionIndex = idx;
        }
      });
    });
  }

  get currentPrescriptionId(): string {
    return this.prescriptionIds[this.currentPrescriptionIndex];
  }
  get currentPrescription(): any {
    return this.batchData?.prescriptions?.find((rx: any) => rx.rxId === this.currentPrescriptionId);
  }
  get currentPatientName(): string {
    return this.groupedMedications[this.currentPrescriptionId]?.patientName;
  }
  get currentMedicines(): any[] {
    return this.groupedMedications[this.currentPrescriptionId]?.medicines || [];
  }
  get isLastPrescription(): boolean {
    return this.currentPrescriptionIndex === this.prescriptionIds.length - 1;
  }
  get isFirstPrescription(): boolean {
    return this.currentPrescriptionIndex === 0;
  }

  jumpToPrescription(event: any) {
    const prescriptionId = event.target.value;
    const index = this.prescriptionIds.indexOf(prescriptionId);
    if (index !== -1) {
      this.currentPrescriptionIndex = index;
    }
  }

  nextPatient() {
    if (this.currentPrescriptionIndex < this.prescriptionIds.length - 1) {
      this.currentPrescriptionIndex++;
    }
  }

  previousPatient() {
    if (this.currentPrescriptionIndex > 0) {
      this.currentPrescriptionIndex--;
    }
  }

  finishScan() {
    if (!confirm('Finish scanning this batch?')) return;
    if (this.isMpsBatch) this.printCompletedBags(true);
    const scanned = true;
    this.isScanComplete = scanned;
    this.isScanFinished.emit(scanned);
    this.printPackedList.emit();
    this.activatedRoute.paramMap.subscribe((params) => {
      this.batchId = params.get('batchId') || '';
      const status = 'Completed';
      this._batchservice.updateBatchStatus(this.batchId, status).subscribe();
    });
  }

  onBarcodeScanned(barcode: string) {
    // View mode (a finished batch opened to reprint bag labels) changes nothing: a scan would
    // reopen the batch.
    if (this.viewOnly) {
      this.showAlert(isBatchPick(this.batchData) && !CLOSED_STATUSES.includes(this.batchData?.status)
        ? 'This is a Batch Pick batch: scan it on the pick and verify screens.'
        : 'This batch is finished: it is open for reprinting bag labels only.', 'warning');
      return;
    }
    // Until the batch has loaded (it starts as {}) it isn't known whether it is finished.
    if (!this.batchData?.prescriptions) return;
    const prescriptionId = this.currentPrescriptionId;
    const currentGroup = this.groupedMedications[prescriptionId];

    this._batchservice.matchScan(this.batchId, prescriptionId, barcode).subscribe((result) => {
      if (!result) {
        this.showAlert('Scan match failed. Please try again.', 'error');
        return;
      }

      if (result.status === 'matched' && result.matchedMedication) {
        const medication = currentGroup.medicines.find(
          (med: any) => med.medicationId === result.matchedMedication!.medicationId,
        );
        if (medication) {
          this.markMedicationScanned(prescriptionId, medication, barcode);
        }
        return;
      }

      // MPS and do-not-substitute lines need the prescribed product itself: no swap is offered.
      if (result.status === 'different_product') {
        const prescribed = result.matchedMedication?.medicationName;
        this.showAlert(
          `Different product than prescribed${prescribed ? ` — scan ${prescribed}` : ''}. Return this pack.`,
          'error',
        );
        return;
      }

      this.wrongMedicineId = barcode;
      this.wrongMedicinePrescriptionId = prescriptionId;
      this.wrongMedicineLookup = result.lookup ?? null;
      this.wrongMedicineMatchStatus = result.status;
      this.wrongMedicineMatchMethod = result.matchMethod ?? null;

      if (
        (result.status === 'ambiguous' || result.status === 'partial_match') &&
        result.candidates?.length
      ) {
        this.wrongMedicineUnscannedMeds = result.candidates.map((c) => ({
          medicationId: c.medicationId,
          medicationName: c.medicationName,
          dosage: c.dosage,
          instruction: c.instruction,
          quantity: c.quantity,
        }));
      } else if (result.status === 'partial_match' && result.matchedMedication) {
        const med = currentGroup.medicines.find(
          (m: any) => m.medicationId === result.matchedMedication!.medicationId,
        );
        this.wrongMedicineUnscannedMeds = med ? [med] : [];
      } else {
        this.wrongMedicineUnscannedMeds = this.getUnscannedMeds(prescriptionId, currentGroup);
      }
      const offered = this.wrongMedicineUnscannedMeds;
      this.wrongMedicineUnscannedMeds = offered.filter((m: any) => this.canTakeScan(m.medicationId));
      this.exactProductNote = this.wrongMedicineUnscannedMeds.length === offered.length ? ''
        : result.status === 'partial_match' && result.matchMethod === 'ingredient'
          ? `Wrong strength${result.matchedMedication ? ` for ${result.matchedMedication.medicationName}` : ''}. Return this pack and scan the prescribed strength.`
          : 'Lines on MPS batches and do-not-substitute lines need the prescribed product, so they can\'t be swapped. Return this pack and scan the prescribed product.';

      this.showWrongMedicineModal = true;
    });
  }

  private getUnscannedMeds(prescriptionId: string, currentGroup: any) {
    return currentGroup.medicines.filter((med: any) => {
      const status = this.scanStatuses[prescriptionId]?.[med.medicationId];
      return status !== 'SCANNED' && status !== 'REPLACED';
    });
  }

  private markMedicationScanned(prescriptionId: string, medication: any, barcode: string) {
    const updatePayload = {
      batchId: this.batchId,
      prescriptions: [
        {
          rxId: prescriptionId,
          medications: [{ medicationId: medication.medicationId, isAvailable: true }],
        },
      ],
    };

    this._batchservice.updateAvailability(updatePayload).subscribe({
      next: () => {
        medication.isAvailable = true;
        this.scanStatuses[prescriptionId][medication.medicationId] = 'SCANNED';
        this.printMedication = {
          medicationName: medication.medicationName ?? '',
          medicineDosage: medication.dosage ?? '',
          instruction: medication.instruction ?? '',
          medicationId: medication.medicationId ?? '',
          quantity: medication.quantity ?? null,
          patientName: this.groupedMedications[prescriptionId]?.patientName ?? '',
          labelDate: this.formatLabelDate(this.getPrescriptionDate(prescriptionId)),
          marking: medication.marking ?? null,
        };

        if (this._nsTray.isConnected) {
          this.printSelectedPrinter('print-medicine');
          // MPS batches get a bag label instead (a bag can span prescriptions).
          if (!this.isMpsBatch && this.isPrescriptionScanned(prescriptionId)) {
            this.printPackLabel = this.createPrintPackLabel(prescriptionId);
            this.printSelectedPrinter('print-pack-label');
          }
        }
        if (this.isMpsBatch) this.printCompletedBags();
        if (this.isPrescriptionScanned(prescriptionId)) {
          if (this.currentPrescriptionIndex < this.prescriptionIds.length - 1) {
            this.currentPrescriptionIndex++;
          }
        }

        this._batchservice.updateBatchStatus(this.batchId, 'PartialComplete').subscribe();
      },
      error: () => {
        this.showAlert(`Failed to update availability for ${barcode}. Please scan again.`, 'error');
      },
    });
  }

  onWrongMedicineDismiss() {
    this.showWrongMedicineModal = false;
    this.showAlert('Please return the wrong medication and scan the correct one.', 'warning');
    this.wrongMedicineId = '';
    this.wrongMedicinePrescriptionId = '';
    this.wrongMedicineLookup = null;
    this.wrongMedicineUnscannedMeds = [];
    this.wrongMedicineMatchStatus = null;
    this.wrongMedicineMatchMethod = null;
    this.exactProductNote = '';
    this.modalClosed.emit();
  }

  onWrongMedicineSwitch(targetMed: any) {
    if (!this.canTakeScan(targetMed.medicationId)) {
      this.onWrongMedicineDismiss();
      this.showAlert(
        `${this.isMpsBatch ? 'Swaps are not allowed on MPS batches' : `${targetMed.medicationName} is do not substitute`}. ` +
        'Return this pack and scan the prescribed product.', 'warning');
      return;
    }
    // A line the server suggested is a copy: work on the prescription's own line.
    targetMed = this.groupedMedications[this.wrongMedicinePrescriptionId]?.medicines
      ?.find((m: any) => m.medicationId === targetMed.medicationId) ?? targetMed;
    // On an exact-product line the only confirmation allowed is which line an exact match is
    // for: that is a scan of the prescribed product, not a swap.
    if (this.isExactProductLine(targetMed.medicationId)) {
      const prescriptionId = this.wrongMedicinePrescriptionId;
      const barcode = this.wrongMedicineId;
      this.showWrongMedicineModal = false;
      this.modalClosed.emit();
      this.markMedicationScanned(prescriptionId, targetMed, barcode);
      return;
    }
    this.showWrongMedicineModal = false;
    this.modalClosed.emit();
    const prescriptionId = this.wrongMedicinePrescriptionId;
    const scannedName = this.wrongMedicineLookup?.product_name || this.wrongMedicineId;
    const scannedDosage = this.wrongMedicineLookup?.dosage_form || '';

    const updatePayload = {
      batchId: this.batchId,
      prescriptions: [{
        rxId: prescriptionId,
        medications: [{ medicationId: targetMed.medicationId, isAvailable: true }],
      }],
    };

    this._batchservice.updateAvailability(updatePayload).subscribe({
      next: () => {
        targetMed.isAvailable = true;
        targetMed.replacedBy = scannedName;
        targetMed.replacedByDosage = scannedDosage;
        this.scanStatuses[prescriptionId][targetMed.medicationId] = 'REPLACED';
        this.showAlert(
          `Switched: ${scannedName} replaces ${targetMed.medicationName}.`,
          'success'
        );

        this.printMedication = {
          medicationName: scannedName,
          medicineDosage: scannedDosage,
          instruction: targetMed.instruction ?? '',
          medicationId: targetMed.medicationId ?? '',
          quantity: targetMed.quantity ?? null,
          patientName: this.groupedMedications[prescriptionId]?.patientName ?? '',
          labelDate: this.formatLabelDate(this.getPrescriptionDate(prescriptionId)),
          marking: targetMed.marking ?? null,
        };

        if (this._nsTray.isConnected) {
          this.printSelectedPrinter('print-medicine');
          if (!this.isMpsBatch && this.isPrescriptionScanned(prescriptionId)) {
            this.printPackLabel = this.createPrintPackLabel(prescriptionId);
            this.printSelectedPrinter('print-pack-label');
          }
        }
        if (this.isMpsBatch) this.printCompletedBags();
        if (this.isPrescriptionScanned(prescriptionId)) {
          if (this.currentPrescriptionIndex < this.prescriptionIds.length - 1) {
            this.currentPrescriptionIndex++;
          }
        }

        this._batchservice.updateBatchStatus(this.batchId, 'PartialComplete').subscribe();
      },
      error: () => {
        this.showAlert('Failed to update replacement. Please try again.', 'error');
      },
    });

    this.wrongMedicineId = '';
    this.wrongMedicinePrescriptionId = '';
    this.wrongMedicineLookup = null;
    this.wrongMedicineUnscannedMeds = [];
    this.wrongMedicineMatchStatus = null;
    this.wrongMedicineMatchMethod = null;
  }

  onAddUnknownMedication() {
    this.showWrongMedicineModal = false;
    this.showAddMedicationForm = true;
    this.addMedForm = { productName: '', manufacturer: '', dosageForm: '', activeSubstances: '', packSize: '' };
    this.addMedWarnings = {};
    this.drugNameSuggestions = [];
    this.manufacturerSuggestions = [];

    // Pre-load reference data
    this._lookupservice.getDosageForms().subscribe(forms => this.dosageFormOptions = forms);
    this._lookupservice.getManufacturers().subscribe(mfrs => this.allManufacturers = mfrs);

    // Focus the drug name input after Angular renders the modal
    setTimeout(() => {
      this.drugNameInputRef?.nativeElement?.focus();
    }, 100);
  }

  onDrugNameInput() {
    clearTimeout(this.drugNameTimer);
    const q = this.addMedForm.productName.trim();
    if (q.length < 2) {
      this.drugNameSuggestions = [];
      this.addMedWarnings.drugName = undefined;
      return;
    }
    this.drugNameTimer = setTimeout(() => {
      this._lookupservice.searchDrugNames(q).subscribe(results => {
        this.drugNameSuggestions = results;
        // Show warning if no exact match
        const exact = results.some(r => r.name.toLowerCase() === q.toLowerCase());
        this.addMedWarnings.drugName = exact ? undefined : 'Drug name not found in known databases — please verify spelling';
      });
    }, 300);
  }

  selectDrugName(name: string) {
    this.addMedForm.productName = name;
    this.drugNameSuggestions = [];
    this.addMedWarnings.drugName = undefined;
  }

  onManufacturerInput() {
    const q = this.addMedForm.manufacturer.trim().toLowerCase();
    if (q.length < 1) {
      this.manufacturerSuggestions = [];
      this.addMedWarnings.manufacturer = undefined;
      return;
    }
    this.manufacturerSuggestions = this.allManufacturers
      .filter(m => m.toLowerCase().includes(q))
      .slice(0, 10);
    const exact = this.allManufacturers.some(m => m.toLowerCase() === q);
    this.addMedWarnings.manufacturer = exact ? undefined : 'Manufacturer not found in known databases — please verify';
  }

  selectManufacturer(name: string) {
    this.addMedForm.manufacturer = name;
    this.manufacturerSuggestions = [];
    this.addMedWarnings.manufacturer = undefined;
  }

  onSaveNewMedication() {
    if (!this.addMedForm.productName.trim()) return;
    this.addMedSaving = true;
    const payload = {
      product_name: this.addMedForm.productName.trim(),
      barcode: this.wrongMedicineId,
      manufacturer: this.addMedForm.manufacturer.trim(),
      dosage_form: this.addMedForm.dosageForm,
      active_substances: this.addMedForm.activeSubstances.trim(),
      pack_size: this.addMedForm.packSize.trim(),
    };
    this._lookupservice.registerMedication(payload).subscribe(result => {
      this.addMedSaving = false;
      if (result.ok) {
        this.showAddMedicationForm = false;
        this.showAlert('Medication registered successfully.', 'success');
        // Re-lookup the barcode — should now be found
        this._lookupservice.lookup(this.wrongMedicineId).subscribe(lookupResult => {
          this.wrongMedicineLookup = lookupResult;
          this.showWrongMedicineModal = true;
        });
      } else {
        this.showAlert(result.error || 'Failed to register medication.', 'error');
      }
    });
  }

  onCancelAddMedication() {
    this.showAddMedicationForm = false;
    this.showWrongMedicineModal = true;
  }

  isPrescriptionScanned(prescriptionId: string): boolean {
    const meds = this.groupedMedications[prescriptionId]?.medicines || [];
    return meds.every(
      (med: any) => {
        const status = this.scanStatuses[prescriptionId]?.[med.medicationId];
        return status === 'SCANNED' || status === 'REPLACED';
      },
    );
  }

  getScannedCount(prescriptionId: string): number {
    return (
      this.groupedMedications[prescriptionId]?.medicines.filter(
        (med: any) => {
          const status = this.scanStatuses[prescriptionId]?.[med.medicationId];
          return status === 'SCANNED' || status === 'REPLACED';
        },
      ).length || 0
    );
  }

  toggleCollapse(prescriptionId: string) {
    this.expandedPrescriptionId =
      this.expandedPrescriptionId === prescriptionId ? null : prescriptionId;
  }

  onCancel(medicine: any): void {
    if (this.viewOnly) return;
    const prescriptionId = this.currentPrescriptionId;
    // A printed bag label lists what the bag should hold; taking an item out makes it wrong.
    const labelledBag = this.isMpsBatch && medicine.bagId && this.printedBagIds.has(medicine.bagId)
      ? medicine.bagId : null;
    if (labelledBag && !confirm(
      `Bag ${labelledBag}'s label has already printed. Remove ${medicine.medicationName} anyway? ` +
      'The bag will be labelled again once it is complete.')) {
      return;
    }
    const updatePayload = {
      batchId: this.batchId,
      prescriptions: [
        {
          rxId: prescriptionId,
          medications: [{ medicationId: medicine.medicationId, isAvailable: false }],
        },
      ],
    };
    this._batchservice.updateAvailability(updatePayload).subscribe({
      next: () => {
        medicine.isAvailable = false;
        medicine.replacedBy = undefined;
        medicine.replacedByDosage = undefined;
        this.scanStatuses[prescriptionId][medicine.medicationId] = 'PENDING';
        if (medicine.bagId) this.triedBagIds.delete(medicine.bagId);
        if (labelledBag) {
          this.printedBagIds.delete(labelledBag);
          this.savePrintedBags();
        }
      },
    });
  }

  openBagReprint() {
    this.showBagReprint = true;
  }

  /** Bags printed from the Reprint dialog count as labelled, so Finish doesn't print them again. */
  onBagsReprinted(bagIds: string[]) {
    bagIds.forEach(id => this.printedBagIds.add(id));
    this.savePrintedBags();
  }

  closeBagReprint() {
    this.showBagReprint = false;
    this.modalClosed.emit();
  }

  /** Per bag: lines scanned, and whether its labels printed (MPS batches only). */
  get bagStatus(): (BagProgress & { labelled: boolean })[] {
    return bagProgress(this.batchData, (rxId, medicationId) => this.isLineScanned(rxId, medicationId))
      .map(b => ({ ...b, labelled: this.printedBagIds.has(b.bagId) }));
  }

  private isLineScanned(rxId: string, medicationId: string): boolean {
    const status = this.scanStatuses[rxId]?.[medicationId];
    return status === 'SCANNED' || status === 'REPLACED';
  }

  private completeBags(): string[] {
    return completeBagIds(this.batchData, (rxId, medicationId) => this.isLineScanned(rxId, medicationId));
  }

  /**
   * Prints every bag that is now fully scanned and hasn't been labelled yet. While scanning a bag
   * is tried once (so a printer problem isn't re-reported on every scan); Finish retries them all.
   */
  private printCompletedBags(retry = false) {
    const ready = this.completeBags().filter(bagId =>
      !this.printedBagIds.has(bagId) && !this.printingBagIds.has(bagId) && (retry || !this.triedBagIds.has(bagId)));
    if (ready.length === 0) return;
    ready.forEach(id => { this.printingBagIds.add(id); this.triedBagIds.add(id); });
    this._bagLabels.printBags(this.batchData, ready).then(printed => {
      printed.forEach(id => this.printedBagIds.add(id));
      this.savePrintedBags();
    }, () => {
      // Not printed: Finish tries again, and Reprint covers it.
      this.showAlert(`Bag label${ready.length === 1 ? '' : 's'} ${ready.join(', ')} didn't print.`, 'error');
    }).finally(() => ready.forEach(id => this.printingBagIds.delete(id)));
  }

  private get printedBagsKey(): string {
    return `safescan.mps.printedBags.${this.batchData?.batchId ?? this.batchId}`;
  }

  private loadPrintedBags(): string[] | null {
    try {
      const saved = localStorage.getItem(this.printedBagsKey);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  }

  private savePrintedBags(): void {
    try { localStorage.setItem(this.printedBagsKey, JSON.stringify([...this.printedBagIds])); } catch { /* private mode */ }
  }

  printSelectedPrinter(printId: string): void {
    const lines = this.buildLabelLines(printId);
    if (!lines) return;
    this._nsTray.print(lines);
  }

  private getPrescriptionDate(prescriptionId: string): Date {
    const rx = this.batchData?.prescriptions?.find((p: any) => p.rxId === prescriptionId);
    const raw = rx?.issuedDate ?? rx?.dispensedDate;
    if (raw) {
      const parsed = new Date(raw);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    }
    return new Date();
  }

  private createPrintPackLabel(prescriptionId: string) {
    const group = this.groupedMedications[prescriptionId];
    const patientIndex = Math.max(this.prescriptionIds.indexOf(prescriptionId) + 1, 1);
    const itemCount = group?.medicines?.length ?? 0;
    return {
      patientLabel: `Pat ${patientIndex}`,
      patientName: group?.patientName ?? '',
      labelDate: this.formatPackLabelDate(this.getPrescriptionDate(prescriptionId)),
      itemCountText: `${itemCount} Item${itemCount === 1 ? '' : 's'}`,
    };
  }

  private formatPackLabelDate(date: Date): string {
    const weekday = date.toLocaleString('en-GB', { weekday: 'short' }).toUpperCase();
    const day = date.getDate();
    const month = date.toLocaleString('en-GB', { month: 'long' });
    const monthFormatted = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
    return `${weekday}, ${day}, ${monthFormatted}, ${date.getFullYear()}`;
  }

  /** "6 Sep 19", the same as the bag labels (the browser's en-GB says "Sept"). */
  private formatLabelDate(date: Date): string {
    return labelDate(date);
  }

  private buildLabelLines(printId: string): RichTextLine[] | null {
    if (printId === 'print-medicine' && this.printMedication) {
      const med = this.printMedication;
      return [
        {
          segments: [
            { text: med.quantity?.toString() || '', size: 7.6, width: 10, align: 'left', wrap: false, marginLeft: 2 },
            { text: [med.medicationName, med.marking?.trim()].filter(Boolean).join(' '), size: 7.6, width: 55, align: 'center', wrap: true },
          ],
          offsetTop: 3,
        },
        { text: med.medicineDosage || '', size: 7.6, align: 'center' },
        { text: med.instruction || '', size: 7.6, align: 'center' },
        {
          segments: [
            { text: med.labelDate, size: 7.6, width: 14, align: 'left', wrap: false, marginLeft: 2 },
            { text: (med.patientName || ''), size: 7.6, width: 55, align: 'left', bold: true, wrap: true },
          ],
          valign: 'bottom', offsetBottom: 7, segmentValign: 'bottom',
        },
      ];
    } else if (printId === 'print-pack-label' && this.printPackLabel) {
      const pack = this.printPackLabel;
      return [
        {
          segments: [
            { text: pack.patientLabel, size: 7.6, width: 10, align: 'left', wrap: false, marginLeft: 2 },
            { text: (pack.patientName || '').trim(), size: 7.6, width: 55, align: 'center', bold: true, wrap: true },
          ],
          offsetTop: 3,
        },
        {
          segments: [
            { text: pack.labelDate, size: 7.6, width: 13, align: 'left', wrap: false, marginLeft: 2 },
            { text: pack.itemCountText, size: 7.6, width: 54, align: 'right', wrap: true },
          ],
          valign: 'bottom', offsetBottom: 7, segmentValign: 'bottom',
        },
      ];
    }
    console.warn(`[SafeScan Print] Nothing to print for ${printId}`);
    return null;
  }
}
